<script>
    import { Handle, NodeResizer, Position } from "@xyflow/svelte";
    import { getContext } from "svelte";
    import { iconInner } from "./icons.js";

    let { data, selected = false } = $props();

    // Supplied by the flow via context — NOT on node data, which xyflow probes with
    // structuredClone (a callback there makes it warn about deep reactivity).
    const flow = getContext("zar-research-card") ?? {};
    const canEdit = flow.canEdit === true;

    // What the queue is doing to THIS card, if anything. Reading a page takes as long as it
    // takes; showing which step it's on is the difference between a wait that makes sense and
    // one that looks like the app has stopped.
    const capture = $derived(flow.captureOf?.(data.entryId) ?? null);
    // Read off this card's OWN data, never derived from the parent's node array: a card that
    // derives from `nodes` re-renders whenever xyflow writes a measurement into it, and a card
    // that sizes itself to its content changes that measurement BY re-rendering. That circle
    // is effect_update_depth_exceeded — sizeParents stamps these facts onto the node instead.
    const nested = $derived(data.nested === true);
    const runbookRelationships = $derived(
        data.runbookRelationships ?? {
            items: [],
            total: 0,
            requiresApproval: false,
        },
    );

    /*
        Whether the file behind this card was actually read. A card holding an unread PDF has to
        LOOK unread — otherwise it reads as evidence, gets asked about, and the answer comes
        from the filename. Each state names a different fix, because "couldn't read it" and
        "the converter is off" are not the same problem.
    */
    const UNREAD = {
        reading: "Reading the file…",
        scanned: "No text layer — scanned",
        service_down: "Not read — converter offline",
        failed: "Could not be read",
        unsupported: "No text to read",
    };
    const unread = $derived(UNREAD[data.extraction] ?? null);
    const holding = $derived((data.childCount ?? 0) > 0);

    /*
        Who this card is assigned to, resolved to people whichever shape it arrived in: the
        server hydrates assignments into {id, name, color}, but a LIVE assignment lands as an
        operation carrying raw user ids, because ids are what get stored.

        This declaration once went missing while the template below still referenced it — a
        patch anchored on code a revert had removed, applied with a replace that no-ops
        silently — and the ReferenceError killed the whole canvas at mount, taking every live
        listener with it.
    */
    const assignees = $derived(flow.peopleFrom?.(data.assignees) ?? []);

    /**
     * What this card should LOOK like.
     *
     * A uniform box for everything makes a canvas of photographs, folders and documents read
     * as forty identical rectangles you have to inspect one at a time. Recognising the thing
     * at a glance is most of what a visual workspace is for, so the chrome follows the
     * content: a picture is a picture, a folder looks like a folder, a document looks like a
     * page. Text keeps the card because text needs a frame to be legible.
     */
    const variant = $derived(
        data.kind === "folder"
            ? "folder"
            : data.mediaUrl && data.isImage && !data.isPortrait
              ? "image"
              : data.mediaUrl && data.isVideo
                ? "video"
                : data.mediaUrl &&
                    /pdf|word|excel|powerpoint|document|sheet|presentation|text\//.test(
                        data.mime ?? "",
                    )
                  ? "document"
                  : data.mediaUrl
                    ? "file"
                    : data.cardType === "research_groups"
                      ? "group"
                      : "text",
    );

    /** A group's colour, as an accent the card paints itself with. */
    const GROUP_COLORS = {
        slate: "#64748b",
        amber: "#d97706",
        emerald: "#059669",
        sky: "#0284c7",
        violet: "#7c3aed",
        rose: "#e11d48",
    };
    const groupAccent = $derived(
        GROUP_COLORS[data.groupColor] ?? GROUP_COLORS.slate,
    );

    /** A date reads as a fact; "2026-03-02" reads as a database row. */
    const eventDate = $derived(
        data.occurredOn
            ? new Date(data.occurredOn + "T00:00:00").toLocaleDateString(
                  undefined,
                  { year: "numeric", month: "short", day: "numeric" },
              )
            : null,
    );

    // Card look per research content type (and note kind).
    const styles = {
        research_topics: { accent: "#6366f1", label: "Topic", icon: "💡" },
        research_sources: { accent: "#06b6d4", label: "Source", icon: "📰" },
        research_notes: { accent: "#a1a1aa", label: "Note", icon: "📝" },
        research_questions: {
            accent: "#f59e0b",
            label: "Question",
            icon: "❓",
        },
        research_claims: { accent: "#0f766e", label: "Claim", icon: "⚖️" },
        research_fallacies: {
            accent: "#dc2626",
            label: "Logic check",
            icon: "⚠️",
        },
        research_groups: { accent: "#64748b", label: "Group", icon: "🗂️" },
        research_people: { accent: "#d946ef", label: "Person", icon: "👤" },
        research_events: { accent: "#0284c7", label: "Event", icon: "📅" },
        research_tasks: { accent: "#7c3aed", label: "Task", icon: "☑️" },
        research_hypotheses: {
            accent: "#2563eb",
            label: "Hypothesis",
            icon: "🔬",
        },
        research_definitions: {
            accent: "#78716c",
            label: "Definition",
            icon: "📖",
        },
    };
    const kindTints = { idea: "#f59e0b", quote: "#8b5cf6", insight: "#84cc16" };

    const initialsOf = (name) =>
        String(name ?? "?")
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((part) => part[0] ?? "")
            .join("")
            .toUpperCase() || "?";

    /** Kinds that describe the ATTACHMENT, not the card. A person with a photo is a Person. */
    const FILE_KINDS = new Set(["image", "file", "video"]);
    // Any OTHER content type added as a card gets a generic look labelled by its type name.
    const GENERIC = { accent: "#0ea5e9", label: "Content", icon: "🗂️" };

    /*
        A custom type's own look, set on the content type and hydrated onto the node.

        Read off this card's OWN data — never from the parent's node array, which is the
        dependency that produces effect_update_depth_exceeded. A type with no appearance set
        yields null and everything below falls back exactly as it did before.
    */
    const custom = $derived(data.cardStyle ?? null);
    const shape = $derived(custom?.shape ?? "box");

    const style = $derived(
        styles[data.cardType] ?? {
            ...GENERIC,
            label: data.typeName ?? "Content",
        },
    );
    const accent = $derived(
        custom?.accent
            ? custom.accent
            : data.cardType === "research_notes" && kindTints[data.kind]
              ? kindTints[data.kind]
              : style.accent,
    );
    // File/image supporting docs override the icon so they read at a glance.
    // A portrait already shows what it is; the paperclip is for cards whose file you can't see.
    const icon = $derived(
        data.isPortrait
            ? style.icon
            : data.kind === "image"
              ? "🖼️"
              : data.kind === "file"
                ? "📎"
                : // The type's own icon outranks the generic folder — that generic look on every custom
                  // type is what made a diagram forty identical rectangles.
                  custom?.icon || style.icon,
    );
    // A claim leads with its evidence status; a logic check with its severity; a task with
    // where it stands; else review/readiness.
    const badge = $derived(
        data.claimStatus ??
            (data.cardType === "research_fallacies" ? data.severity : null) ??
            (data.cardType === "research_tasks" ? data.taskStatus : null) ??
            (data.cardType === "runbook_steps" ? data.authoringStatus : null) ??
            (data.cardType === "research_hypotheses"
                ? data.hypothesisStatus
                : null) ??
            data.reviewStatus ??
            data.readiness ??
            null,
    );
    const confidence = $derived(
        data.cardType === "research_claims" ? data.confidence : null,
    );
    // Finished tasks fade + strike through, so open work stands out on the canvas.
    const done = $derived(
        data.cardType === "research_tasks" && data.taskStatus === "done",
    );

    // Open the entry in the Livewire reading pane (double-click, so single-click/drag select).
    function openReader() {
        if (data.entryId)
            window.Livewire?.dispatch("research-open-card", {
                entryId: data.entryId,
            });
    }

    const relationshipLabel = (item) =>
        item.relation === "prerequisite"
            ? "Prerequisite"
            : item.relation === "predecessor"
              ? "Predecessor"
              : item.kind === "success"
                ? "On success"
                : item.kind === "failure"
                  ? "On failure"
                  : "Successor";

    const relationshipGlyph = (item) =>
        item.relation === "prerequisite"
            ? "◆"
            : item.relation === "predecessor"
              ? "←"
              : "→";

    function focusRelationship(event, item) {
        event.stopPropagation();
        flow.focusRunbookNode?.(item.nodeId);
    }

    // ── Rename in place ──────────────────────────────────────────────────────────
    // A new card mounts straight into its name field; an existing one gets there by
    // double-clicking the title. Everything else on the card still opens the pane.
    let editing = $state(data.editing === true);
    let draft = $state(data.title ?? "");

    function startRename(event) {
        if (!canEdit) return;
        event.stopPropagation(); // don't also open the reading pane
        draft = data.title ?? "";
        editing = true;
    }

    function commit() {
        if (!editing) return;
        editing = false;
        flow.rename?.(data.entryId, draft);
    }

    function onNameKey(event) {
        if (event.key === "Enter") {
            event.preventDefault();
            commit();
        } else if (event.key === "Escape") {
            event.preventDefault();
            editing = false;
            draft = data.title ?? "";
            flow.rename?.(data.entryId, data.title ?? "");
        }
    }

    /** Focus + select the name field as soon as it appears. */
    function nameField(node) {
        node.focus();
        node.select();
    }
</script>

<!--
    Resizable, but only while selected — permanent handles on forty cards is visual noise, and
    the gesture people reach for is "click it, then drag its corner". A card that holds other
    cards needs more room than one holding a sentence, and having to re-drag it on every load
    is what makes a canvas feel disposable.
-->
{#if canEdit}
    <!-- A resize has to be told to the flow: nothing else fires, so the size the user just
         chose was never saved, and the card had to be re-dragged on every load. -->
    <NodeResizer
        minWidth={180}
        minHeight={90}
        isVisible={selected}
        onResizeEnd={() => flow.resized?.(data.entryId)}
        lineClass="rc-resize-line"
        handleClass="rc-resize-handle"
    />
{/if}
<!--
    Per-card tools, on hover.
    Everything here was already possible — through a right-click menu, a double-click, or the
    panel — which is another way of saying it was possible if you already knew. A floating
    strip puts the three things people actually do to a card (open it, rename it, delete it)
    where the card is. It appears on hover so forty cards don't carry forty toolbars.
-->
{#if canEdit}
    <div
        class="rc-tools"
        role="toolbar"
        tabindex="-1"
        onpointerdown={(e) => e.stopPropagation()}
        ondblclick={(e) => e.stopPropagation()}
    >
        <button
            type="button"
            title="Open this card"
            aria-label="Open this card"
            onclick={openReader}>✎</button
        >
        <button
            type="button"
            title="Rename"
            aria-label="Rename"
            onclick={startRename}>Aa</button
        >
        <!--
            Out of the group. `extent: 'parent'` clamps a nested card inside its parent, which
            is what stops it drifting out by accident — and also makes dragging it out
            impossible. Without a button there was no way back at all.
        -->
        {#if nested}
            <button
                type="button"
                title="Move out of its group"
                aria-label="Move out of its group"
                onclick={(e) => {
                    e.stopPropagation();
                    flow.unnest?.(data.entryId);
                }}>⇱ out</button
            >
        {/if}
        <button
            type="button"
            title="Assign this card"
            aria-label="Assign this card"
            onclick={(e) => {
                e.stopPropagation();
                window.Livewire?.dispatch?.("research-open-assign", {
                    entryId: data.entryId,
                });
            }}>👤</button
        >
        {#if holding}
            <button
                type="button"
                title="Release everything inside this card"
                aria-label="Release everything inside this card"
                onclick={(e) => {
                    e.stopPropagation();
                    flow.ungroup?.(data.entryId);
                }}>⇱ ungroup</button
            >
        {/if}
        <button
            type="button"
            title="Delete this card"
            aria-label="Delete this card"
            onclick={(e) => {
                e.stopPropagation();
                flow.remove?.(data.entryId);
            }}>🗑</button
        >
    </div>
{/if}
<div
    class="research-card"
    class:selected
    class:rc-done={done}
    data-variant={variant}
    data-shape={shape}
    style="--accent: {variant === 'group' ? groupAccent : accent}"
    ondblclick={openReader}
    role="button"
    tabindex="-1"
>
    <!-- A handle on every side. Connections are Loose, so any of these can start OR end a
         link — which is what lets one card carry many connections (several sources into one
         claim, one topic out to many questions) without them all stacking on one edge. -->
    <Handle type="target" position={Position.Left} id="left" />
    <Handle type="target" position={Position.Top} id="top" />
    <div class="rc-head">
        {#if variant === "folder"}<span class="rc-folder-tab"></span>{/if}
        <!-- A named icon draws as a stroke outline in the card's accent; anything else is an
             emoji glyph. The SVG inner comes from the server-installed registry (icons.js),
             a repo constant — never user markup — so @html is safe by construction. -->
        {#if iconInner(icon)}
            <svg
                class="rc-icon rc-icon-svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">{@html iconInner(icon)}</svg
            >
        {:else}
            <span class="rc-icon">{icon}</span>
        {/if}
        <span class="rc-type"
            >{FILE_KINDS.has(data.kind)
                ? style.label
                : (data.kind ?? style.label)}</span
        >
        <!-- Who made it: a model's proposal must never look like a person's decision. -->
        {#if data.origin === "ai"}<span
                class="rc-origin"
                title="The AI proposed this — it's a suggestion until you agree"
                >✨</span
            >
        {:else if data.origin === "system"}<span
                class="rc-origin"
                title="Computed from the workspace by a rule">⚙</span
            >{/if}
        <!-- Waiting on a person. -->
        {#if data.needsYou}<span
                class="rc-todo"
                title="Needs you — open the card to see what">●</span
            >{/if}
        {#if badge}<span class="rc-badge" data-badge={badge}
                >{badge.replaceAll?.("_", " ") ?? badge}</span
            >{/if}
        {#if data.runbookGroup}
            <button
                type="button"
                class="rc-branch"
                disabled={data.runbookLoadingChildren}
                aria-busy={data.runbookLoadingChildren}
                aria-expanded={!data.runbookCollapsed}
                aria-label="{data.runbookCollapsed
                    ? 'Expand'
                    : 'Collapse'} {data.title}"
                title="{data.runbookCollapsed
                    ? 'Show'
                    : 'Hide'} {data.runbookDescendantCount} nested node{data.runbookDescendantCount ===
                1
                    ? ''
                    : 's'}"
                onpointerdown={(event) => event.stopPropagation()}
                onclick={(event) => {
                    event.stopPropagation();
                    flow.toggleRunbookBranch?.(`rbflow-${data.entryId}`);
                }}
            >
                <span class:rc-branch-open={!data.runbookCollapsed}>›</span>
                {data.runbookDescendantCount}
            </button>
        {/if}
    </div>
    {#if data.marker}
        <!-- Not a card: the two ends of a generated flow. A reader arriving at a diagram of
             forty boxes needs to know which one is first, and a procedure that stops without
             saying so reads as one somebody forgot to finish. -->
        <div class="rc-marker" class:rc-marker-end={data.marker === "end"}>
            {data.title}
        </div>
    {:else if editing}
        <!-- svelte-ignore a11y_autofocus -->
        <input
            class="rc-name"
            bind:value={draft}
            use:nameField
            onkeydown={onNameKey}
            onblur={commit}
            onclick={(e) => e.stopPropagation()}
            ondblclick={(e) => e.stopPropagation()}
            onpointerdown={(e) => e.stopPropagation()}
            aria-label="Card name"
        />
    {:else}
        <div
            class="rc-title"
            class:rc-renamable={canEdit}
            ondblclick={startRename}
            role="presentation"
            title={canEdit ? "Double-click to rename" : null}
        >
            {#if data.stepNumber}<span
                    class="rc-step"
                    title="Step {data.stepNumber}">{data.stepNumber}</span
                >{/if}{data.title}
        </div>
    {/if}
    {#if data.runbookLoadingChildren}
        <div class="rc-child-loading" role="status" aria-live="polite">
            <div>
                <span></span><span></span><span></span>
            </div>
            <small>Loading children…</small>
        </div>
    {/if}
    {#if data.runbookBlockerCount > 0}
        <div
            class="rc-blocked"
            title="This node waits for prerequisite work elsewhere in the visible flow"
        >
            waiting on {data.runbookBlockerCount} prerequisite{data.runbookBlockerCount ===
            1
                ? ""
                : "s"}
        </div>
    {/if}
    {#if runbookRelationships.requiresApproval || runbookRelationships.items.length}
        <div
            class="rc-relationships"
            role="group"
            aria-label="Step relationships"
            onpointerdown={(event) => event.stopPropagation()}
            ondblclick={(event) => event.stopPropagation()}
        >
            {#if runbookRelationships.requiresApproval}
                <div
                    class="rc-approval"
                    title="Approval is required before this step can run"
                >
                    <span>◆</span> Approval required
                </div>
            {/if}
            {#each runbookRelationships.items as item (`${item.relation}:${item.nodeId}`)}
                <button
                    type="button"
                    class="rc-relationship"
                    title="{relationshipLabel(item)}: {item.stepNumber
                        ? `${item.stepNumber} `
                        : ''}{item.title}"
                    onclick={(event) => focusRelationship(event, item)}
                >
                    <span class="rc-relation-kind"
                        >{relationshipGlyph(item)}</span
                    >
                    <span class="rc-relation-label"
                        >{relationshipLabel(item)}</span
                    >
                    {#if item.stepNumber}<span class="rc-relation-number"
                            >{item.stepNumber}</span
                        >{/if}
                    <span class="rc-relation-title">{item.title}</span>
                </button>
            {/each}
            {#if runbookRelationships.total > runbookRelationships.items.length}
                <button
                    type="button"
                    class="rc-relationship-more"
                    onclick={(event) => {
                        event.stopPropagation();
                        openReader();
                    }}>Show more</button
                >
            {/if}
        </div>
    {/if}
    {#if confidence}
        <div class="rc-conf" data-conf={confidence}>
            <span></span><span></span><span></span>
            {confidence}
        </div>
    {/if}
    {#if data.mediaUrl && data.isPortrait}
        <!--
            A person's picture is their identity on a canvas. Twelve relatives named "Ana",
            "Ana (aunt)" and "Ana M." are unreadable; twelve faces are obvious at a glance —
            so it sits round and beside the name rather than as a banner across the card.
        -->
        <img
            class="rc-portrait"
            src={data.mediaUrl}
            alt={data.title}
            loading="lazy"
        />
    {:else if data.mediaUrl && data.isImage}
        <img
            class="rc-thumb"
            src={data.mediaUrl}
            alt={data.title}
            loading="lazy"
        />
    {:else if data.mediaUrl && data.isVideo}
        <!--
            A few seconds of the video, muted and on hover. A filename tells you nothing about
            what you filmed; two seconds of it tells you everything — and playing on hover
            rather than autoplaying keeps a canvas of twenty clips from becoming a wall of
            motion. `preload="metadata"` fetches the header only, so the poster frame costs a
            few KB rather than the whole file.
        -->
        <!-- svelte-ignore a11y_media_has_caption -->
        <video
            class="rc-thumb rc-video"
            src={data.mediaUrl}
            muted
            playsinline
            preload="metadata"
            onmouseenter={(e) => {
                e.currentTarget.currentTime = 0;
                e.currentTarget.play?.().catch(() => {});
            }}
            onmouseleave={(e) => {
                e.currentTarget.pause?.();
                e.currentTarget.currentTime = 0;
            }}
            ontimeupdate={(e) => {
                if (e.currentTarget.currentTime > 4) {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                }
            }}
            title="Hover to preview · open the card to watch it"
        ></video>
    {:else if data.mediaUrl}
        <a
            class="rc-file"
            href={data.mediaUrl}
            target="_blank"
            rel="noopener"
            title={data.title}>📎 {data.title}</a
        >
    {/if}
    <!-- On an event the date is the fact, not metadata: it belongs on the face. -->
    {#if eventDate}
        <div class="rc-date">
            {eventDate}
            {#if data.datePrecision && data.datePrecision !== "exact"}<span
                    class="rc-date-flag">{data.datePrecision}</span
                >{/if}
        </div>
    {/if}
    {#if data.domain}<div class="rc-meta">{data.domain}</div>{/if}
    {#if data.excerpt}<div class="rc-excerpt">{data.excerpt}</div>{/if}

    <!-- A note shows its text. Reading a canvas shouldn't mean opening forty cards. -->
    {#if variant === "text" && !data.excerpt && data.preview}
        <div class="rc-preview">{data.preview}</div>
    {/if}

    {#if data.access === "locked" || data.access === "assigned" || assignees.length}
        <!-- Who it belongs to, and whether it's governed. The lock is INFORMATION here: the op
             handler is what actually refuses the change. -->
        <div class="rc-owners">
            {#if data.access === "locked"}<span
                    class="rc-lock"
                    title="Locked — only the workspace owner can change this"
                    >🔒</span
                >
            {:else if data.access === "assigned"}<span
                    class="rc-lock"
                    title="Only the people assigned can change this">🔑</span
                >{/if}
            {#each assignees.slice(0, 3) as person (person.id)}
                <span
                    class="rc-owner"
                    style="background:{person.color}"
                    title={person.name}>{initialsOf(person.name)}</span
                >
            {/each}
        </div>
    {/if}

    <!-- What this step costs and when it is due. Two different clocks, deliberately labelled:
         "~5m" is how long the work takes, "by 15m" is the minute of the RUN it must be done by,
         which is the one that can be read against the runbook's SLA. -->
    {#if data.stepMinutes != null || data.stepDeadline != null}
        <div class="rc-timing">
            {#if data.stepMinutes != null}
                <span
                    title="Estimated {data.stepMinutes} minute{data.stepMinutes ===
                    1
                        ? ''
                        : 's'} of work">⏱ {data.stepMinutes}m</span
                >
            {/if}
            {#if data.stepDeadline != null}
                <span
                    class="rc-due"
                    title="Must be finished by minute {data.stepDeadline} of the run"
                    >⏳ by {data.stepDeadline}m</span
                >
            {/if}
        </div>
    {/if}

    {#if (data.commentCount ?? 0) > 0 || (data.attachmentCount ?? 0) > 0}
        <div class="rc-counts">
            {#if (data.commentCount ?? 0) > 0}
                <span
                    class="rc-comments"
                    title="{data.commentCount} comment{data.commentCount === 1
                        ? ''
                        : 's'}">💬 {data.commentCount}</span
                >
            {/if}
            <!-- A runbook step's attachments: the diagram, the screenshot, the config to paste.
                 On a flow you read the boxes, so a step whose files are only visible once the
                 panel is open is a step whose diagram nobody opens. -->
            {#if (data.attachmentCount ?? 0) > 0}
                <span
                    class="rc-comments"
                    title="{data.attachmentCount} attachment{data.attachmentCount ===
                    1
                        ? ''
                        : 's'}">📎 {data.attachmentCount}</span
                >
            {/if}
        </div>
    {/if}
    <!-- Drill-down: shown ONLY on cards that already have their own canvas (creating one is a
         deliberate act from the card panel). Click goes through Livewire, which owns routing. -->
    {#if data.hasDetail}
        <button
            type="button"
            class="rc-detail"
            title="Open this card's own canvas"
            onclick={(e) => {
                e.stopPropagation();
                window.Livewire?.dispatch?.("research-open-detail", {
                    id: data.entryId,
                });
            }}
        >
            ⌕ detail canvas
        </button>
    {/if}

    {#if data.childCount > 0}
        <div class="rc-holds">
            holds {data.childCount} card{data.childCount === 1 ? "" : "s"}
        </div>
    {/if}

    {#if capture}
        <div class="rc-capture" aria-live="polite">
            <span class="rc-capture-dot"></span>
            <span>{capture.line}</span>
        </div>
    {:else if unread}
        <div
            class="rc-capture"
            class:rc-unread={data.extraction !== "reading"}
            title={data.extractionNote ?? ""}
        >
            {#if data.extraction === "reading"}<span class="rc-capture-dot"
                ></span>{:else}<span>⚠</span>{/if}
            <span>{unread}</span>
        </div>
    {/if}
    <Handle type="source" position={Position.Right} id="right" />
    <Handle type="source" position={Position.Bottom} id="bottom" />
</div>

<style>
    /*
        Theme tokens for the whole canvas. Driven by the `.dark` class the app puts on <html>,
        NOT by `prefers-color-scheme` — the app lets you choose a theme independently of the
        machine, and the CSS system colours (`canvas`, `canvastext`) only ever know the machine.
    */
    :global(:root) {
        --rc-surface: #ffffff;
        --rc-ink: #18181b;
        --rc-muted: #52525b;
        --rc-faint: #71717a;
        --rc-line: rgba(128, 128, 128, 0.22);
        --rc-shadow: rgba(0, 0, 0, 0.08);
    }
    :global(.dark) {
        --rc-surface: #1b1b1f;
        --rc-ink: #f4f4f5;
        --rc-muted: #a1a1aa;
        --rc-faint: #a1a1aa;
        --rc-line: rgba(255, 255, 255, 0.16);
        --rc-shadow: rgba(0, 0, 0, 0.5);
    }

    .research-card {
        /* The portrait is positioned against the card, so the card has to be the anchor. */
        position: relative;
        /*
            Grow to the content by default. `height: 100%` against a node that measures its own
            content is circular, and with overflow hidden it CLIPPED — a folder listing or a
            note preview lost its last lines on load, which is the one thing a card on a canvas
            must not do. min-height fills a node that has an explicit size and resolves to
            nothing when it doesn't.
        */
        width: 100%;
        height: auto;
        min-height: 100%;
        min-width: 180px;
        box-sizing: border-box;
        overflow: visible;
        border-radius: 12px;
        border: 1px solid rgba(128, 128, 128, 0.3);
        border-left: 4px solid var(--accent);
        background: var(--rc-surface);
        color: var(--rc-ink);
        padding: 10px 12px;
        box-shadow: 0 2px 8px var(--rc-shadow);
        font-family: inherit;
    }
    .research-card.selected {
        border-color: var(--accent);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 40%, transparent);
    }
    .rc-head {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 4px;
    }
    .rc-icon {
        font-size: 12px;
    }
    .rc-type {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--accent);
    }
    .rc-origin {
        font-size: 10px;
        opacity: 0.75;
    }
    .rc-holds {
        margin-top: 6px;
        font-size: 10px;
        color: var(--rc-faint);
        border-top: 1px dashed rgba(120, 120, 130, 0.35);
        padding-top: 5px;
    }
    .research-card:has(.rc-portrait) .rc-name,
    .research-card:has(.rc-portrait) .rc-title {
        padding-right: 46px;
    }
    .rc-tools {
        position: absolute;
        top: -32px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 2px;
        padding: 3px;
        border-radius: 9px;
        background: var(--rc-surface);
        color: var(--rc-ink);
        border: 1px solid var(--rc-line);
        box-shadow: 0 4px 14px var(--rc-shadow);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.12s;
        z-index: 3;
    }
    /* Shown when the NODE is hovered or selected — the strip sits outside the card element,
       so it can't key off the card's own hover. A touch user never hovers, hence selection. */
    :global(.svelte-flow__node:hover) .rc-tools,
    :global(.svelte-flow__node.selected) .rc-tools {
        opacity: 1;
        pointer-events: auto;
    }
    .rc-tools button {
        border: 0;
        background: transparent;
        cursor: pointer;
        font-size: 11px;
        line-height: 1;
        padding: 4px 6px;
        border-radius: 6px;
        color: inherit;
    }
    .rc-tools button:hover {
        background: rgba(128, 128, 128, 0.16);
    }

    /* A card the user sized keeps that size, content or not — they asked for it. */
    :global(.rf-sized) .research-card {
        height: 100%;
        overflow: hidden;
    }

    /* Media crops on purpose: the rounded corners have to bite. */
    .research-card[data-variant="image"],
    .research-card[data-variant="video"] {
        overflow: hidden;
    }

    :global(.rc-resize-line) {
        border-color: var(--accent);
        opacity: 0.5;
    }
    :global(.rc-resize-handle) {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        background: var(--rc-surface);
        border: 1.5px solid var(--accent);
    }

    /* ── Content-shaped cards ────────────────────────────────────────────────────
       A uniform box for everything makes a canvas of photos, folders and documents
       read as forty identical rectangles. The chrome follows the content instead. */

    /* An image is a picture, not a picture in a box: no border, no header strip,
       rounded corners, and the name only when you look at it. */
    .research-card[data-variant="image"],
    .research-card[data-variant="video"] {
        padding: 0;
        border: none;
        border-left: none;
        background: transparent;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18);
        border-radius: 14px;
    }
    .research-card[data-variant="image"] .rc-head,
    .research-card[data-variant="video"] .rc-head {
        display: none;
    }
    .research-card[data-variant="image"] .rc-thumb,
    .research-card[data-variant="video"] .rc-thumb {
        display: block;
        width: 100%;
        height: 100%;
        max-height: none;
        object-fit: cover;
        border-radius: 14px;
        margin: 0;
    }
    /* The title rides over the foot of the image rather than taking space beside it. */
    .research-card[data-variant="image"] .rc-title,
    .research-card[data-variant="video"] .rc-title {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        padding: 14px 10px 7px;
        border-radius: 0 0 14px 14px;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.72));
        color: #fff;
        font-size: 11px;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        opacity: 0;
        transition: opacity 0.15s;
    }
    .research-card[data-variant="image"]:hover .rc-title,
    .research-card[data-variant="video"]:hover .rc-title,
    .research-card[data-variant="image"].selected .rc-title,
    .research-card[data-variant="video"].selected .rc-title {
        opacity: 1;
    }

    /* A folder looks like a folder: the tab above the body, warm, and clearly a container. */
    .research-card[data-variant="folder"] {
        background: linear-gradient(#fdf0d0, #f7dfa8);
        border: 1px solid #e3c169;
        border-left: 1px solid #e3c169;
        border-radius: 4px 12px 12px 12px;
        padding-top: 14px;
    }
    :global(.dark) .research-card[data-variant="folder"] {
        background: linear-gradient(#4a3f24, #3d3420);
        border-color: #6b5a2f;
    }
    .rc-folder-tab {
        position: absolute;
        top: -9px;
        left: -1px;
        width: 74px;
        height: 11px;
        background: #f7dfa8;
        border: 1px solid #e3c169;
        border-bottom: none;
        border-radius: 6px 8px 0 0;
    }
    :global(.dark) .rc-folder-tab {
        background: #4a3f24;
        border-color: #6b5a2f;
    }
    .research-card[data-variant="folder"] .rc-title {
        font-weight: 600;
    }

    /* A document looks like a page: white sheet, thin rule, turned corner. */
    .research-card[data-variant="document"] {
        background: #fff;
        border: 1px solid #d8d8dd;
        border-left: 1px solid #d8d8dd;
        border-radius: 3px 3px 8px 8px;
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
    }
    .research-card[data-variant="document"]::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        border-width: 0 16px 16px 0;
        border-style: solid;
        border-color: #e4e4e7 transparent transparent #e4e4e7;
        box-shadow: -1px 1px 1px rgba(0, 0, 0, 0.06);
    }
    /* A page is a page in both themes — a document reads as paper, so it stays light-faced
       but loses the glare, rather than becoming a dark rectangle indistinguishable from a note. */
    :global(.dark) .research-card[data-variant="document"] {
        background: #e7e5e4;
        color: #27272a;
        border-color: #a8a29e;
    }
    :global(.dark) .research-card[data-variant="document"] .rc-type,
    :global(.dark) .research-card[data-variant="document"] .rc-preview {
        color: #57534e;
    }
    :global(.dark) .research-card[data-variant="document"]::after {
        border-color: #d6d3d1 transparent transparent #d6d3d1;
    }
    :global(.dark) .research-card[data-variant="document"] .rc-head {
        border-bottom-color: #d6d3d1;
    }
    .research-card[data-variant="document"] .rc-head {
        border-bottom: 1px solid #ececef;
        padding-bottom: 4px;
    }

    /* Text: softer than before, and it SHOWS its text — the point of a note on a canvas is
       reading it without opening it. Height follows the content. */
    .research-card[data-variant="text"] {
        border-radius: 14px;
        border-color: rgba(128, 128, 128, 0.18);
        box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.06),
            0 6px 16px rgba(0, 0, 0, 0.05);
    }
    .rc-preview {
        margin-top: 6px;
        font-size: 11px;
        line-height: 1.45;
        color: var(--rc-muted);
        white-space: pre-wrap;
    }
    /*
        Only a card the user sized clamps its text — it has a fixed height to respect, and
        cutting at a line boundary reads better than a half-line sliced by overflow. An
        auto-sizing card grows instead: what the server sent is already trimmed, so showing all
        of it is bounded, and hiding part of it is just a card that lies about its contents.
    */
    :global(.rf-sized) .rc-preview {
        display: -webkit-box;
        -webkit-line-clamp: 12;
        line-clamp: 12;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    /* A group is a labelled region, not a card: tinted, dashed, and clearly a container. */
    .research-card[data-variant="group"] {
        background: color-mix(in srgb, var(--accent) 8%, transparent);
        border: 1.5px dashed color-mix(in srgb, var(--accent) 55%, transparent);
        border-left: 1.5px dashed
            color-mix(in srgb, var(--accent) 55%, transparent);
        border-radius: 16px;
        box-shadow: none;
    }
    .research-card[data-variant="group"] .rc-title {
        font-weight: 700;
        color: var(--accent);
    }

    .rc-date {
        margin-top: 5px;
        font-size: 11px;
        font-weight: 600;
        color: var(--accent);
        display: flex;
        align-items: center;
        gap: 5px;
    }
    .rc-date-flag {
        font-weight: 500;
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #b45309;
        background: #fef3c7;
        border-radius: 999px;
        padding: 1px 5px;
    }
    :global(.dark) .rc-date-flag {
        color: #fcd34d;
        background: rgba(180, 83, 9, 0.22);
    }
    .rc-portrait {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 40px;
        height: 40px;
        border-radius: 999px;
        object-fit: cover;
        background: var(--rc-line);
        box-shadow:
            0 0 0 2px var(--rc-surface),
            0 1px 3px rgba(0, 0, 0, 0.18);
    }
    .rc-video {
        background: #000;
        cursor: pointer;
    }
    .rc-capture {
        display: flex;
        align-items: center;
        gap: 5px;
        margin-top: 6px;
        padding-top: 6px;
        border-top: 1px dashed rgba(120, 120, 130, 0.35);
        font-size: 10px;
        line-height: 1.3;
        color: var(--rc-muted);
    }
    /*
        Shapes for custom types. A datastore that looks like a cylinder and a service that
        looks like a hexagon are readable at a glance; the same rounded box for both is a list
        pretending to be a diagram.

        Chrome only — none of these change how a card SIZES itself, so the measure/layout path
        (sizing.js) is untouched, which is the part that has broken before.
    */
    .research-card[data-shape="cylinder"] {
        border-radius: 50% / 14px;
        padding-top: 14px;
        background-image: linear-gradient(
            color-mix(in srgb, var(--accent) 10%, transparent),
            transparent 40%
        );
    }
    .research-card[data-shape="cylinder"]::before {
        content: "";
        position: absolute;
        left: -1px;
        right: -1px;
        top: -1px;
        height: 14px;
        border: 1px solid var(--rc-line);
        border-radius: 50%;
        background: var(--rc-surface);
    }
    .research-card[data-shape="pill"] {
        border-radius: 999px;
        padding-left: 16px;
        padding-right: 16px;
    }
    /* A clip-path can't be combined with a border, so the hexagon's edge is a wrapper drawn
       behind it — a plain clipped card would lose its outline entirely. */
    .research-card[data-shape="hexagon"] {
        clip-path: polygon(
            14px 0,
            calc(100% - 14px) 0,
            100% 50%,
            calc(100% - 14px) 100%,
            14px 100%,
            0 50%
        );
        border-radius: 0;
        padding-left: 20px;
        padding-right: 20px;
        background: color-mix(in srgb, var(--accent) 8%, var(--rc-surface));
        border-color: transparent;
        box-shadow: none;
    }
    .research-card[data-shape="hexagon"].selected {
        background: color-mix(in srgb, var(--accent) 18%, var(--rc-surface));
    }
    /* Flowchart vocabulary: diamond = decision/gateway, parallelogram = data/IO. Both are
       clip-paths, so like the hexagon they trade their border for an accent tint. The diamond
       eats its corners — generous padding keeps the title inside the visible area. */
    .research-card[data-shape="diamond"] {
        clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0 50%);
        border-radius: 0;
        padding: 26px 34px;
        text-align: center;
        background: color-mix(in srgb, var(--accent) 8%, var(--rc-surface));
        border-color: transparent;
        box-shadow: none;
    }
    .research-card[data-shape="diamond"].selected {
        background: color-mix(in srgb, var(--accent) 18%, var(--rc-surface));
    }
    .research-card[data-shape="diamond"] .rc-head {
        justify-content: center;
    }
    .research-card[data-shape="parallelogram"] {
        clip-path: polygon(12px 0, 100% 0, calc(100% - 12px) 100%, 0 100%);
        border-radius: 0;
        padding-left: 18px;
        padding-right: 18px;
        background: color-mix(in srgb, var(--accent) 8%, var(--rc-surface));
        border-color: transparent;
        box-shadow: none;
    }
    .research-card[data-shape="parallelogram"].selected {
        background: color-mix(in srgb, var(--accent) 18%, var(--rc-surface));
    }

    /* A named icon: a stroke outline the size of the emoji it replaces, in the accent. */
    .rc-icon-svg {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        color: var(--accent);
    }

    /* The drill-down affordance — quiet until hovered, in the card's own accent. */
    .rc-detail {
        margin-top: 5px;
        border: none;
        background: color-mix(in srgb, var(--accent) 10%, transparent);
        border-radius: 999px;
        padding: 1px 7px;
        font-size: 10px;
        color: var(--accent);
        cursor: pointer;
    }
    .rc-detail:hover {
        background: color-mix(in srgb, var(--accent) 22%, transparent);
    }

    .rc-marker {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 6px 4px;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #16a34a;
    }

    .rc-marker-end {
        color: #64748b;
    }

    .rc-timing {
        margin-top: 5px;
        display: flex;
        gap: 8px;
        font-size: 10px;
        color: var(--rc-faint);
    }
    .rc-due {
        color: #b45309;
        font-weight: 600;
    }
    .rc-counts {
        margin-top: 5px;
        display: flex;
        gap: 8px;
    }
    .rc-comments {
        font-size: 10px;
        color: var(--rc-faint);
    }
    .rc-owners {
        display: flex;
        align-items: center;
        gap: 3px;
        margin-top: 6px;
    }
    .rc-lock {
        font-size: 10px;
        line-height: 1;
    }
    .rc-owner {
        display: grid;
        place-items: center;
        width: 16px;
        height: 16px;
        border-radius: 999px;
        font-size: 8px;
        font-weight: 700;
        color: #fff;
    }
    .rc-unread {
        color: #b45309;
    }
    :global(.dark) .rc-unread {
        color: #fbbf24;
    }
    .rc-capture-dot {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: #06b6d4;
        animation: rc-pulse 1.1s ease-in-out infinite;
        flex: none;
    }
    @keyframes rc-pulse {
        0%,
        100% {
            opacity: 0.35;
        }
        50% {
            opacity: 1;
        }
    }
    .rc-todo {
        font-size: 12px;
        line-height: 1;
        color: #f59e0b;
    }
    .rc-branch {
        display: inline-flex;
        min-width: 30px;
        min-height: 26px;
        align-items: center;
        justify-content: center;
        gap: 3px;
        margin-left: auto;
        padding: 2px 6px;
        border: 1px solid color-mix(in srgb, var(--accent) 32%, transparent);
        border-radius: 6px;
        background: color-mix(in srgb, var(--accent) 9%, transparent);
        color: var(--accent);
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
    }
    .rc-branch:hover {
        background: color-mix(in srgb, var(--accent) 18%, transparent);
    }
    .rc-branch:disabled {
        cursor: wait;
        opacity: 0.65;
    }
    .rc-branch span {
        font-size: 16px;
        line-height: 10px;
        transition: transform 160ms ease-out;
    }
    .rc-branch-open {
        transform: rotate(90deg);
    }
    .rc-child-loading {
        display: flex;
        align-items: center;
        gap: 7px;
        min-height: 28px;
        margin-top: 6px;
        color: var(--rc-faint);
        font-size: 9px;
    }
    .rc-child-loading > div {
        display: flex;
        flex: 1;
        flex-direction: column;
        gap: 3px;
    }
    .rc-child-loading span {
        display: block;
        height: 4px;
        border-radius: 2px;
        background: color-mix(in srgb, var(--accent) 20%, transparent);
        animation: rc-child-pulse 700ms ease-in-out infinite alternate;
    }
    .rc-child-loading span:nth-child(2) {
        width: 76%;
        animation-delay: 80ms;
    }
    .rc-child-loading span:nth-child(3) {
        width: 52%;
        animation-delay: 160ms;
    }
    .rc-child-loading small {
        white-space: nowrap;
        font-size: 9px;
    }
    @keyframes rc-child-pulse {
        to {
            opacity: 0.3;
        }
    }
    @media (prefers-reduced-motion: reduce) {
        .rc-child-loading span {
            animation: none;
        }
    }
    .rc-blocked {
        width: fit-content;
        margin-top: 6px;
        padding: 2px 7px;
        border: 1px solid rgba(217, 119, 6, 0.34);
        border-radius: 999px;
        background: rgba(245, 158, 11, 0.1);
        color: #b45309;
        font-size: 9px;
        font-weight: 700;
    }
    :global(.dark) .rc-blocked {
        color: #fbbf24;
    }
    .rc-relationships {
        display: grid;
        gap: 3px;
        margin-top: 7px;
        padding-top: 6px;
        border-top: 1px solid var(--rc-line);
    }
    .rc-relationship {
        display: grid;
        grid-template-columns: 12px auto auto minmax(0, 1fr);
        align-items: center;
        gap: 4px;
        min-height: 20px;
        padding: 2px 4px;
        border: 0;
        border-radius: 4px;
        background: transparent;
        color: var(--rc-muted);
        cursor: pointer;
        font-size: 9px;
        text-align: left;
    }
    .rc-relationship:hover,
    .rc-relationship:focus-visible {
        background: color-mix(in srgb, var(--accent) 10%, transparent);
        color: var(--rc-ink);
        outline: none;
    }
    .rc-relation-kind {
        color: var(--accent);
        font-size: 10px;
        text-align: center;
    }
    .rc-relation-label {
        color: var(--rc-faint);
        font-weight: 650;
    }
    .rc-relation-number {
        font-family: ui-monospace, monospace;
        color: var(--accent);
        font-variant-numeric: tabular-nums;
    }
    .rc-relation-title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .rc-approval {
        display: flex;
        align-items: center;
        gap: 5px;
        color: #b45309;
        font-size: 9px;
        font-weight: 700;
    }
    :global(.dark) .rc-approval {
        color: #fbbf24;
    }
    .rc-relationship-more {
        justify-self: start;
        padding: 2px 4px;
        border: 0;
        background: transparent;
        color: var(--accent);
        cursor: pointer;
        font-size: 9px;
        font-weight: 700;
    }
    .rc-relationship-more:hover {
        text-decoration: underline;
    }
    .rc-badge {
        margin-left: auto;
        font-size: 9px;
        font-weight: 600;
        padding: 1px 6px;
        border-radius: 999px;
        border: 1px solid rgba(128, 128, 128, 0.35);
        color: inherit;
        opacity: 0.8;
    }
    :global(.dark) .rc-badge[data-badge="approved"],
    :global(.dark) .rc-badge[data-badge="ready"],
    :global(.dark) .rc-badge[data-badge="supported"],
    :global(.dark) .rc-badge[data-badge="done"] {
        border-color: #4ade80;
        color: #4ade80;
    }
    :global(.dark) .rc-badge[data-badge="doing"],
    :global(.dark) .rc-badge[data-badge="leading"] {
        border-color: #60a5fa;
        color: #60a5fa;
    }
    :global(.dark) .rc-badge[data-badge="refuted"],
    :global(.dark) .rc-badge[data-badge="rejected"],
    :global(.dark) .rc-badge[data-badge="contradicted"],
    :global(.dark) .rc-badge[data-badge="high"] {
        border-color: #f87171;
        color: #f87171;
    }
    :global(.dark) .rc-badge[data-badge="weakened"],
    :global(.dark) .rc-badge[data-badge="unsupported"],
    :global(.dark) .rc-badge[data-badge="medium"] {
        border-color: #fbbf24;
        color: #fbbf24;
    }
    :global(.dark) .rc-badge[data-badge="needs_review"] {
        border-color: #a5b4fc;
        color: #a5b4fc;
    }
    .rc-badge[data-badge="approved"],
    .rc-badge[data-badge="ready"],
    .rc-badge[data-badge="supported"],
    .rc-badge[data-badge="done"] {
        border-color: #16a34a;
        color: #16a34a;
        opacity: 1;
    }
    .rc-badge[data-badge="doing"],
    .rc-badge[data-badge="leading"] {
        border-color: #2563eb;
        color: #2563eb;
        opacity: 1;
    }
    .rc-badge[data-badge="refuted"] {
        border-color: #dc2626;
        color: #dc2626;
        opacity: 1;
    }
    .rc-badge[data-badge="weakened"] {
        border-color: #f59e0b;
        color: #b45309;
        opacity: 1;
    }
    .rc-badge[data-badge="rejected"],
    .rc-badge[data-badge="contradicted"] {
        border-color: #dc2626;
        color: #dc2626;
        opacity: 1;
    }
    .rc-badge[data-badge="unsupported"] {
        border-color: #f59e0b;
        color: #b45309;
        opacity: 1;
    }
    .rc-badge[data-badge="needs_review"] {
        border-color: #6366f1;
        color: #6366f1;
        opacity: 1;
    }
    .rc-badge[data-badge="high"] {
        border-color: #dc2626;
        color: #dc2626;
        opacity: 1;
    }
    .rc-badge[data-badge="medium"] {
        border-color: #f59e0b;
        color: #b45309;
        opacity: 1;
    }
    .rc-conf {
        margin-top: 5px;
        display: flex;
        align-items: center;
        gap: 3px;
        font-size: 9px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
    }
    .rc-conf span {
        width: 14px;
        height: 4px;
        border-radius: 2px;
        background: rgba(128, 128, 128, 0.3);
    }
    .rc-conf[data-conf="low"] span:nth-child(1),
    .rc-conf[data-conf="medium"] span:nth-child(-n + 2),
    .rc-conf[data-conf="high"] span:nth-child(-n + 3) {
        background: var(--accent);
    }
    /*
        A step's number, in front of its name. Outside the title text, not prepended to it:
        the number is the runbook's, the title is the step's, and renaming a card must not
        hand someone "2.3 Restart php-fpm" to edit.
    */
    .rc-step {
        margin-right: 0.4em;
        font-variant-numeric: tabular-nums;
        font-weight: 700;
        opacity: 0.5;
    }

    .rc-title {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.3;
    }
    .rc-title.rc-renamable:hover {
        text-decoration: underline dotted;
        text-underline-offset: 2px;
        cursor: text;
    }
    .rc-name {
        width: 100%;
        box-sizing: border-box;
        font: inherit;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.3;
        padding: 1px 4px;
        margin: -1px -4px;
        border-radius: 5px;
        border: 1px solid var(--accent);
        background: canvas;
        color: inherit;
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 22%, transparent);
    }
    /* A finished task reads as struck-through + faded, so open work stands out. */
    .research-card.rc-done {
        opacity: 0.6;
    }
    .research-card.rc-done .rc-title {
        text-decoration: line-through;
    }
    .rc-thumb {
        margin-top: 6px;
        width: 100%;
        max-height: 140px;
        object-fit: cover;
        border-radius: 8px;
        display: block;
    }
    .rc-file {
        margin-top: 6px;
        display: inline-block;
        max-width: 100%;
        font-size: 11px;
        font-weight: 500;
        padding: 3px 8px;
        border-radius: 7px;
        background: rgba(128, 128, 128, 0.14);
        color: inherit;
        text-decoration: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    .rc-file:hover {
        background: rgba(99, 102, 241, 0.16);
    }
    .rc-meta {
        margin-top: 2px;
        font-size: 10px;
        opacity: 0.6;
    }
    .rc-excerpt {
        margin-top: 4px;
        font-size: 11px;
        line-height: 1.35;
        opacity: 0.75;
    }
    :global(.rf-sized) .rc-excerpt {
        display: -webkit-box;
        -webkit-line-clamp: 4;
        line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
